/**
 * DueDiligenceDotsInline Component
 *
 * Displays due diligence status dots inline for table list view.
 * Uses intersection observer to only calculate DD for visible rows.
 * Shows grouped building vs unit differentiation in tooltips.
 */

import { useState, useEffect, useRef } from "react";
import { Spinner, Tooltip } from "@heroui/react";
import { BuildingDTO } from "shared/types/building";
import { useBuildingDueDiligence } from "shared/hooks/useBuildingDueDiligence";
import ColoredDot from "../components/operationalDashboard/components/coloredDot";
import { Buildings, Stack } from "@phosphor-icons/react";

interface DueDiligenceDotsInlineProps {
  building: BuildingDTO;
}

interface DueDiligenceSource {
  id: string;
  name: string;
  label: string;
  isBuilding?: boolean;
  isUnit?: boolean;
  unitType?: string;
}

/**
 * Helper to group sources by building vs units for tooltip display
 */
const groupSourcesByType = (sources: DueDiligenceSource[]) => {
  if (!sources || sources.length === 0) {
    return { buildingItems: [], unitGroups: {} };
  }

  // Separate building-level from unit-level items
  const buildingItems = sources.filter(source => source.isBuilding);
  const unitItems = sources.filter(source => source.isUnit);

  // Group unit items by unit name
  const unitGroups: Record<string, DueDiligenceSource[]> = {};
  unitItems.forEach(source => {
    const unitName = source.name || 'Onbekende unit';
    if (!unitGroups[unitName]) {
      unitGroups[unitName] = [];
    }
    unitGroups[unitName].push(source);
  });

  return { buildingItems, unitGroups };
};

/**
 * Render grouped tooltip content with building/unit differentiation
 */
const TooltipContent = ({ title, count, sources }: { title: string; count: number; sources: DueDiligenceSource[] }) => {
  const { buildingItems, unitGroups } = groupSourcesByType(sources);

  return (
    <div className="p-2 max-w-md max-h-80 overflow-y-auto">
      <p className="font-medium mb-2 sticky top-0 bg-inherit">{title} ({count})</p>

      {buildingItems.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center gap-1 mb-1 text-xs font-medium text-neutral-primary">
            <Buildings weight="duotone" size={14} />
            <span>Gebouw algemeen</span>
          </div>
          <div className="space-y-1 ml-5">
            {buildingItems.map((source, idx) => (
              <p key={idx} className="text-xs text-neutral-secondary-mid">{source.label}</p>
            ))}
          </div>
        </div>
      )}

      {Object.keys(unitGroups).length > 0 && (
        <div className="space-y-2">
          {Object.entries(unitGroups).map(([unitName, unitSources]) => (
            <div key={unitName}>
              <div className="flex items-center gap-1 mb-1 text-xs font-medium text-neutral-primary">
                <Stack weight="duotone" size={14} />
                <span>{unitName}</span>
              </div>
              <div className="space-y-1 ml-5">
                {unitSources.map((source, idx) => (
                  <p key={idx} className="text-xs text-neutral-secondary-mid">{source.label}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const DueDiligenceDotsInline = ({ building }: DueDiligenceDotsInlineProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  // Only calculate DD when row becomes visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once visible, stop observing
          observer.disconnect();
        }
      },
      {
        rootMargin: '200px', // Start loading 200px before visible
        threshold: 0.01
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const {
    dotValues: numericValues,
    isLoading,
    isRevalidating,
  } = useBuildingDueDiligence(building, {
    includeUnits: true,
    allowStale: true,
    skip: !isVisible, // Only calculate when visible
  });

  // Don't render anything if not visible yet (saves performance)
  if (!isVisible) {
    return (
      <div ref={elementRef} className="flex items-center gap-1">
        <span className="text-xs text-neutral-secondary-mid">...</span>
      </div>
    );
  }

  if (isLoading || !numericValues) {
    return (
      <div className="flex items-center gap-1">
        <Spinner size="sm" />
        <span className="text-xs text-neutral-secondary-mid">Berekenen...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 relative">
      {isRevalidating && (
        <div className="absolute -right-6">
          <Spinner size="sm" color="secondary" />
        </div>
      )}

      {/* Green Dot */}
      <Tooltip
        content={
          <TooltipContent
            title="Goed"
            count={numericValues.green.count}
            sources={numericValues.green.sources || []}
          />
        }
        placement="top"
      >
        <div>
          <ColoredDot
            color="green"
            number={numericValues.green.count}
            labels={[]}
            sources={[]}
            hideLabels={true}
          />
        </div>
      </Tooltip>

      {/* Orange Dot */}
      <Tooltip
        content={
          <TooltipContent
            title="Let op"
            count={numericValues.orange.count}
            sources={numericValues.orange.sources || []}
          />
        }
        placement="top"
      >
        <div>
          <ColoredDot
            color="orange"
            number={numericValues.orange.count}
            labels={[]}
            sources={[]}
            hideLabels={true}
          />
        </div>
      </Tooltip>

      {/* Red Dot */}
      <Tooltip
        content={
          <TooltipContent
            title="Actie nodig"
            count={numericValues.red.count}
            sources={numericValues.red.sources || []}
          />
        }
        placement="top"
      >
        <div>
          <ColoredDot
            color="red"
            number={numericValues.red.count}
            labels={[]}
            sources={[]}
            hideLabels={true}
          />
        </div>
      </Tooltip>

      {/* Transparent Dot */}
      <Tooltip
        content={
          <TooltipContent
            title="Niet van toepassing"
            count={numericValues.transparent.count}
            sources={numericValues.transparent.sources || []}
          />
        }
        placement="top"
      >
        <div>
          <ColoredDot
            color="transparent"
            number={numericValues.transparent.count}
            labels={[]}
            sources={[]}
            hideLabels={true}
          />
        </div>
      </Tooltip>
    </div>
  );
};
